import { toNumber } from 'lodash';

import { ServicePaymentType } from '@/modules/HealthPlan/HealthPlan.types';
import {
  HealthPlanRenewal,
  RenewalDeductibleConfig,
  RenewalProcessingTier,
} from '@/modules/Renewals/renewals.types';

import { EditAmountsFormValues } from '../EditAmountsModal.types';

const toAmount = (val: string | undefined): number | undefined =>
  val != null && val !== '' ? toNumber(val) : undefined;

/**
 * Merges editable form amounts back into the original HealthPlanRenewal.
 * Read-only data (names, ids, configs) come from `original` by positional index.
 * Form only provides the amount strings the user edited.
 */
const convertFormValuesToHealthPlanRenewal = (
  original: HealthPlanRenewal,
  formValues: EditAmountsFormValues
): HealthPlanRenewal => {
  const processingTiers: RenewalProcessingTier[] = (original.processingTiers || []).map((tier, tierIdx) => ({
    ...tier,
    serviceConfigs: (tier.serviceConfigs || []).map((svc, svcIdx) => ({
      ...svc,
      paymentRules: (svc.paymentRules || []).map((rule, ruleIdx) => {
        const amount = toAmount(
          formValues.tiers?.[tierIdx]?.services?.[svcIdx]?.paymentRules?.[ruleIdx]?.amount
        );
        return {
          ...rule,
          ...(svc.paymentType === ServicePaymentType.PERCENTAGE
            ? { percentage: amount != null ? amount / 100 : undefined }
            : { maxAmount: amount }),
        };
      }),
    })),
  }));

  const deductibleConfigs: RenewalDeductibleConfig[] = (original.deductibleConfigs || []).map(
    (config, configIdx) => ({
      ...config,
      triggerConfigs: (config.triggerConfigs || []).map((trigger, triggerIdx) => ({
        ...trigger,
        thresholdAmount: toAmount(formValues.coverageLevels?.[configIdx]?.triggers?.[triggerIdx]?.amount),
      })),
      autoFundingConfigs: (config.autoFundingConfigs || []).map((funding, fundingIdx) => ({
        ...funding,
        autoEnrollmentAmount: toAmount(
          formValues.coverageLevels?.[configIdx]?.plans?.[fundingIdx]?.autoFundingAmount
        ),
        perPersonSpendLimitAmount: toAmount(
          formValues.coverageLevels?.[configIdx]?.plans?.[fundingIdx]?.perPersonSpendLimit
        ),
      })),
      categorySpendLimits: (config.autoFundingConfigs || []).flatMap((funding, fundingIdx) => {
        const formCsls =
          formValues.coverageLevels?.[configIdx]?.plans?.[fundingIdx]?.categorySpendLimits || [];
        const originalCsls = (config.categorySpendLimits || []).filter(
          (csl) => csl.planId === funding.planId
        );
        return originalCsls.map((csl, cslIdx) => ({
          ...csl,
          categorySpendLimit: toAmount(formCsls[cslIdx]?.amount),
        }));
      }),
    })
  );

  return {
    ...original,
    processingTiers,
    deductibleConfigs,
  };
};

export default convertFormValuesToHealthPlanRenewal;
